import React from 'react';

/**
 * One-time consent modal shown before the user's first photo analysis.
 * The stored permission covers later photos the user chooses to share, until
 * they withdraw it in Account settings.
 *
 * Props:
 *  - isOpen: boolean
 *  - onAllow: () => void   — user grants consent
 *  - onNotNow: () => void  — user declines (for now)
 */
export default function ImageConsentModal({ isOpen, onAllow, onNotNow, onAlternative, context = 'shade-match' }) {
  const [showAreYouSure, setShowAreYouSure] = React.useState(false);
  const isPublicColourStudy = context === 'colour-analysis-public-study';
  const isColourAnalysis = context === 'colour-analysis' || isPublicColourStudy;
  const isColourStudy = context === 'colour-study';
  const isColourPhoto = isColourAnalysis || isColourStudy;

  if (!isOpen) return null;

  const handleNotNow = () => {
    if (isColourAnalysis) { setShowAreYouSure(false); onNotNow(); return; }
    if (!showAreYouSure) {
      setShowAreYouSure(true);
      return;
    }
    // Second tap — actually dismiss
    setShowAreYouSure(false);
    onNotNow();
  };

  const handleAllow = () => {
    setShowAreYouSure(false);
    onAllow();
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 20,
          width: '100%',
          maxWidth: 400,
          padding: '28px 24px 24px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          textAlign: 'center',
        }}
      >
        {!showAreYouSure ? (
          /* ── Main consent screen ── */
          <>
            <h3 style={{
              fontSize: 18, fontWeight: 700, color: '#1A1A1A',
              margin: '0 0 8px',
            }}>
              {isColourStudy ? 'Use this photo for the local study' : isColourAnalysis ? 'Allow photo analysis' : 'Allow photo analysis?'}
            </h3>

            <p style={{
              fontSize: 14, color: '#666', lineHeight: 1.6,
              margin: '0 0 24px',
            }}>
              {isColourStudy
                ? 'The photo is used only in this browser session to show the colour comparisons. It is not uploaded or saved.'
                : isPublicColourStudy
                ? 'Son:mi uses an AI service to analyse the photos you choose and your optional colour notes. If you create an account or sign in, we save your photos and colour report privately so you can return to them.'
                : isColourAnalysis
                ? 'Son:mi uses an AI service to analyse the photos you choose and your optional colour notes. We save your photos and colour report privately to your account so you can return to them.'
                : 'Son:mi will analyse photos you choose to share to provide skin, shade and product guidance. Your images may also help us improve recommendations across skin tones.'}
            </p>

            {!isColourPhoto && <p style={{
              fontSize: 13, color: '#999', lineHeight: 1.5,
              margin: '0 0 20px',
            }}>
              Once allowed, you won’t be asked for every photo. You can change this anytime in{' '}
              <span style={{ fontWeight: 600, color: '#7A5C4A' }}>Account settings</span>.{' '}
              <a
                href="https://sonmi.io/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#7A5C4A', textDecoration: 'underline' }}
              >
                Learn more
              </a>
            </p>}

            {isColourAnalysis && <p style={{ fontSize: 13, color: '#777', lineHeight: 1.5, margin: '0 0 20px' }}>
              <a href="https://docs.google.com/document/d/1LGqi4h-gzjYhQr60E0wo0zmIx6GFK02nEHGv7C_lmE8/edit?tab=t.0" target="_blank" rel="noopener noreferrer" style={{ color: '#7A5C4A', textDecoration: 'underline' }}>Privacy Notice</a>
            </p>}

            {/* Allow button */}
            <button
              onClick={handleAllow}
              style={{
                width: '100%', padding: '14px', border: 'none',
                borderRadius: 100, background: '#341C1C', color: 'white',
                fontSize: 15, fontWeight: 600, cursor: 'pointer',
                marginBottom: 10,
              }}
            >
              Allow photo analysis
            </button>

            {/* Not now button */}
            <button
              onClick={handleNotNow}
              style={{
                width: '100%', padding: '12px', border: 'none',
                borderRadius: 100, background: 'transparent', color: '#AFA99F',
                fontSize: 14, fontWeight: 500, cursor: 'pointer',
              }}
            >
              Not now
            </button>
          </>
        ) : (
          /* ── "Are you sure?" follow-up ── */
          <>
            <h3 style={{
              fontSize: 18, fontWeight: 700, color: '#1A1A1A',
              margin: '0 0 8px',
            }}>
              {isColourPhoto ? 'A photo is required' : "You'll miss your shade match"}
            </h3>

            <p style={{
              fontSize: 14, color: '#666', lineHeight: 1.6,
              margin: '0 0 24px',
            }}>
              {isColourStudy
                ? 'The comparisons need the same photo on both sides. You can return to the study without selecting one.'
                : isColourAnalysis
                ? 'Colour analysis needs one photo to create a profile. You can return and allow access whenever you are ready.'
                : "Without a photo, Son:mi can't find your exact foundation, lipstick, or blush shades — or spot specific skin concerns for you. You can still ask me anything about skincare. You can always change your mind later in Account settings."}
            </p>

            {/* Allow button */}
            <button
              onClick={handleAllow}
              style={{
                width: '100%', padding: '14px', border: 'none',
                borderRadius: 100, background: '#341C1C', color: 'white',
                fontSize: 15, fontWeight: 600, cursor: 'pointer',
                marginBottom: 10,
              }}
            >
              {isColourPhoto ? 'Allow and continue' : 'Allow photo analysis'}
            </button>

            {/* Continue without */}
            <button
              onClick={() => {
                setShowAreYouSure(false);
                if (isColourPhoto && onAlternative) onAlternative();
                else onNotNow();
              }}
              style={{
                width: '100%', padding: '12px', border: 'none',
                borderRadius: 100, background: 'transparent', color: '#AFA99F',
                fontSize: 14, fontWeight: 500, cursor: 'pointer',
              }}
            >
              {isColourStudy ? 'Return to study' : isColourAnalysis ? 'See an example instead' : 'Continue without'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
